import api from "./apiClient";

const getBaseUrl = () =>
	window.__RUNTIME_CONFIG__.REACT_APP_AGENTIC_V2_ENDPOINT;

/**
 * Read a File as a base64 data-URI string.
 * @param {File} file
 * @returns {Promise<string>} e.g. "data:application/pdf;base64,..."
 */
function fileToBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}

/**
 * Generate audiences using the agentic-builder-v2 pipeline
 * @param {Object} params - Generation parameters
 * @param {number} params.siloId - Silo ID to target
 * @param {string} [params.targetingText] - Targeting text content
 * @param {File} [params.rfpFile] - RFP file upload
 * @param {string} [params.model] - Global model ID fallback (overrides per-step defaults for all steps)
 * @param {Object} [params.stepModels] - Per-step model overrides (model IDs only, provider inferred)
 * @param {string} [params.stepModels.extract_strategy] - Model ID for Step 1
 * @param {string} [params.stepModels.plan_audiences] - Model ID for Step 2
 * @param {string} [params.stepModels.build_criteria] - Model ID for Step 3
 * @param {string} [params.stepModels.assemble_audience] - Model ID for Step 4
 * @param {number} [params.temperature=0.7] - Temperature 0-2
 * @param {string} [params.targetingMode='full'] - Targeting mode
 * @returns {Promise<Object>} Generated audiences with debug_info
 */
export async function generateAudiencesV2(params) {
	const {
		siloId,
		targetingText,
		rfpFile,
		model,
		stepModels,
		temperature = 0.7,
		targetingMode = "full",
		includeDebug = false,
	} = params;

	const body = {
		silo_id: Number(siloId),
		temperature: Number(temperature),
		targeting_mode: targetingMode,
		include_debug: includeDebug,
	};

	if (model) body.model = model;
	if (stepModels) body.step_models = stepModels;

	if (targetingText) {
		body.targeting_text = targetingText;
	}

	if (rfpFile) {
		body.rfp_file = await fileToBase64(rfpFile);
		body.rfp_file_name = rfpFile.name;
	}

	const response = await api.request(`${getBaseUrl()}/audiences/generate`, {
		method: "POST",
		body: JSON.stringify(body),
		headers: {
			"Content-Type": "application/json",
		},
	});

	return await response.json();
}

/**
 * Submit like/dislike feedback for a generated audience.
 * @param {Object} params
 * @param {string} params.generationId - UUID of the generation run
 * @param {number} params.audienceIndex - 0-based index
 * @param {"like"|"dislike"} params.rating
 * @param {string[]} [params.reasons] - Reasons for dislike
 * @param {string} [params.details] - Free-text feedback
 * @returns {Promise<Object>} { success: true }
 */
export async function submitAudienceFeedback(params) {
	const { generationId, audienceIndex, rating, reasons, details } = params;

	const body = {
		generation_id: generationId,
		audience_index: audienceIndex,
		rating,
	};

	if (reasons) body.reasons = reasons;
	if (details) body.details = details;

	const response = await api.request(`${getBaseUrl()}/audiences/feedback`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});

	return await response.json();
}

/**
 * Fetch paginated list of all AI generations (admin only).
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=25]
 * @returns {Promise<Object>} { data, total, page, pageSize }
 */
export async function getAdminGenerations({ page = 1, pageSize = 25 } = {}) {
	const response = await api.request(
		`${getBaseUrl()}/admin/generations?page=${page}&page_size=${pageSize}`,
		{ method: "GET" },
	);
	return await response.json();
}

/**
 * Fetch full detail for a single generation (admin only).
 * @param {string} generationId - UUID
 * @returns {Promise<Object>} Generation detail with feedback array
 */
export async function getAdminGenerationDetail(generationId) {
	const response = await api.request(
		`${getBaseUrl()}/admin/generations/${generationId}`,
		{ method: "GET" },
	);
	return await response.json();
}

/**
 * Fetch a single past generation the current user can access (not admin-only).
 * Visibility is scoped by the generation's silo on the backend. Used to reopen
 * the suggested-audience list without re-running the pipeline (ASB-3052).
 * @param {string} generationId - UUID
 * @returns {Promise<Object>} Generation detail including response_json + feedback array
 */
export async function getMyGenerationDetail(generationId) {
	const response = await api.request(
		`${getBaseUrl()}/audiences/generations/${generationId}`,
		{ method: "GET" },
	);
	return await response.json();
}

/**
 * Generate audiences via SSE streaming endpoint.
 * Streams progress events so the UI can show which pipeline step is running.
 *
 * @param {Object} params - Same params as generateAudiencesV2
 * @param {Object} callbacks
 * @param {Function} [callbacks.onProgress] - Called with progress events (step_start / step_complete)
 * @param {Function} [callbacks.onComplete] - Called with the final response JSON
 * @param {Function} [callbacks.onError] - Called with error data
 */
export async function generateAudiencesV2Stream(params, callbacks) {
	const { onProgress, onComplete, onError } = callbacks;
	const {
		siloId,
		targetingText,
		rfpFile,
		model,
		stepModels,
		temperature = 0.7,
		targetingMode = "full",
		includeDebug = false,
	} = params;

	const body = {
		silo_id: Number(siloId),
		temperature: Number(temperature),
		targeting_mode: targetingMode,
		include_debug: includeDebug,
	};

	if (model) body.model = model;
	if (stepModels) body.step_models = stepModels;

	if (targetingText) {
		body.targeting_text = targetingText;
	}

	if (rfpFile) {
		body.rfp_file = await fileToBase64(rfpFile);
		body.rfp_file_name = rfpFile.name;
	}

	const response = await api.request(`${getBaseUrl()}/audiences/generate/stream`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	});

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	let eventType = null;

	while (true) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });

		const lines = buffer.split("\n");
		buffer = lines.pop();
		for (const line of lines) {
			if (line.startsWith("event: ")) {
				eventType = line.slice(7).trim();
			} else if (line.startsWith("data: ")) {
				try {
					const data = JSON.parse(line.slice(6));
					if (eventType === "progress") onProgress?.(data);
					else if (eventType === "complete") onComplete?.(data);
					else if (eventType === "error") onError?.(data);
				} catch {
					// Skip malformed JSON lines
				}
				eventType = null;
			}
		}
	}
}
